'use client';

import { useEffect } from 'react';

// Klaviyo onsite forms inject HTML via inline styles whose cascade gets
// disrupted by our Tailwind/preflight rules:
//   1. `form.klaviyo-form` inline `background-color: #fff` is wiped out by
//      Klaviyo's own `background: none` reset (specificity tie, last-wins).
//   2. The button row's flex chain doesn't grow to the popup width, so the
//      submit button (`width: 100%`) collapses and the label wraps per glyph.
//   3. Klaviyo dashboard "Minimum Height" (500px) is applied to the form's
//      inner wrapper as inline `min-height`, forcing the popup taller than its
//      content and creating empty space below the submit button.
//   4. The dashboard preset CTA color is primary-600; we want primary-700 plus
//      design-system typography/radius to match the rest of the site.
// CSS-only fixes need `:has()` which is unsupported on older browsers, so we
// patch inline styles on every form mutation instead.
//
// Mounted from both the Next.js webapp and the WordPress react-shortcode
// bundle, so the popup looks identical wherever Klaviyo's SDK loads.
const PRIMARY_700 = '#311275';
const PRIMARY_800 = '#250A5D';
// Resolves to GeneralSans on the Next.js webapp where `--general-sans` is
// declared on `:root` (Next.js font loader assigns a hashed family name to
// that variable). On WordPress the variable isn't defined, so the fallback
// uses the literal "General Sans" family that the WP theme already loads via
// @font-face — system fonts are the last resort if neither is available.
const FONT_FAMILY = 'var(--general-sans, "General Sans", system-ui, sans-serif)';
const HOVER_STYLE_ID = 'klaviyo-form-style-fix-hover';
const MOUNTED_FLAG = '__klaviyoFormStyleFixMounted';

type WindowWithFlag = Window & { [MOUNTED_FLAG]?: boolean };

function ensureHoverStylesheet(): void {
  if (document.getElementById(HOVER_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = HOVER_STYLE_ID;
  style.textContent = `
    form.klaviyo-form button[type='button']:hover,
    form.klaviyo-form button[type='submit']:hover {
      background: ${PRIMARY_800} !important;
      background-color: ${PRIMARY_800} !important;
    }
    a[aria-label^='Created with Klaviyo'] {
      opacity: 0.5 !important;
      transition: opacity 200ms ease !important;
    }
    a[aria-label^='Created with Klaviyo']:hover {
      opacity: 1 !important;
    }
    /* The dashboard preset close button is a solid primary-colored circle
       that visually competes with the CTA. Replace with a glassy white pill
       and a neutral × glyph so the close affordance stays obvious without
       fighting the "Shop now" button for attention. */
    /* Klaviyo's offset parent for the close button is the popup wrapper, not
       the form, so top/right values don't translate cleanly to "inside the
       image corner". The values below were tuned visually so the close pill
       lands ~8px inside the image's top-right corner across desktop and
       mobile breakpoints. */
    button.klaviyo-close-form {
      width: 28px !important;
      height: 28px !important;
      top: -16px !important;
      right: 0 !important;
      background: rgba(255, 255, 255, 0.85) !important;
      border-radius: 9999px !important;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12) !important;
      backdrop-filter: blur(8px) !important;
      -webkit-backdrop-filter: blur(8px) !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      transition: background 200ms ease, transform 200ms ease !important;
    }
    button.klaviyo-close-form:hover {
      background: #ffffff !important;
      transform: scale(1.05) !important;
    }
    button.klaviyo-close-form svg {
      display: none !important;
    }
    button.klaviyo-close-form::before {
      content: '×';
      font-size: 18px;
      font-weight: 400;
      color: #1a1a1a;
      line-height: 1;
    }
  `;
  document.head.appendChild(style);
}

function applyKlaviyoPopupTheme(form: HTMLFormElement): void {
  form.style.setProperty('background-color', '#fff', 'important');
  form.style.setProperty('min-height', '0', 'important');
  form.style.setProperty('height', 'auto', 'important');
  form.style.setProperty('padding', '24px', 'important');
  form.style.setProperty('border-radius', '16px', 'important');
  form.style.setProperty('max-width', 'calc(100vw - 32px)', 'important');
  form.style.setProperty('font-family', FONT_FAMILY, 'important');
  form.style.setProperty('box-shadow', '0 25px 50px -12px rgba(0, 0, 0, 0.25)', 'important');

  // Klaviyo writes font-family inline on every element, so inheritance alone
  // can't replace the dashboard preset (Plus Jakarta Sans). Stamp our font
  // token on all descendants of the form.
  form.querySelectorAll<HTMLElement>('*').forEach((el) => {
    el.style.setProperty('font-family', FONT_FAMILY, 'important');
  });

  // Klaviyo applies `transform: scale(...)` and width: 450px on the popup
  // wrapper to fit narrow viewports — that swallows the 16px margins we want
  // on mobile. Cap the wrapper at viewport-minus-32 and disable its scale so
  // our form max-width can take effect.
  let popupWrapper: HTMLElement | null = form;
  for (let i = 0; i < 5 && popupWrapper; i += 1) {
    popupWrapper = popupWrapper.parentElement;
  }
  if (popupWrapper) {
    popupWrapper.style.setProperty('max-width', 'calc(100vw - 32px)', 'important');
    popupWrapper.style.setProperty('width', 'auto', 'important');
    popupWrapper.style.setProperty('transform', 'scale(1)', 'important');
  }

  form.querySelectorAll<HTMLElement>('div').forEach((div) => {
    div.style.setProperty('min-height', '0', 'important');
  });

  const submitButton = form.querySelector<HTMLButtonElement>(
    'button[type="button"], button[type="submit"]',
  );
  if (!submitButton) return;

  submitButton.style.setProperty('width', '100%', 'important');
  submitButton.style.setProperty('align-self', 'stretch', 'important');
  submitButton.style.setProperty('margin-left', '0', 'important');
  submitButton.style.setProperty('margin-right', '0', 'important');
  submitButton.style.setProperty('margin-bottom', '0', 'important');
  submitButton.style.setProperty('background', PRIMARY_700, 'important');
  submitButton.style.setProperty('background-color', PRIMARY_700, 'important');
  submitButton.style.setProperty('color', '#ffffff', 'important');
  submitButton.style.setProperty('font-size', '16px', 'important');
  submitButton.style.setProperty('font-weight', '500', 'important');
  submitButton.style.setProperty('border-radius', '8px', 'important');
  submitButton.style.setProperty('padding', '14px 24px', 'important');
  submitButton.style.setProperty('height', 'auto', 'important');
  submitButton.style.setProperty('transition', 'background-color 200ms ease', 'important');
  submitButton.style.setProperty('cursor', 'pointer', 'important');

  let ancestor = submitButton.parentElement;
  while (ancestor && ancestor !== form) {
    ancestor.style.setProperty('width', '100%', 'important');
    ancestor.style.setProperty('align-self', 'stretch', 'important');
    ancestor.style.setProperty('margin-left', '0', 'important');
    ancestor.style.setProperty('margin-right', '0', 'important');
    ancestor.style.setProperty('margin-bottom', '0', 'important');
    ancestor.style.setProperty('padding-bottom', '0', 'important');
    const parent = ancestor.parentElement;
    const parentDir = parent ? getComputedStyle(parent).flexDirection : '';
    if (parentDir === 'row' || parentDir === 'row-reverse') {
      ancestor.style.setProperty('flex', '1 1 100%', 'important');
    }
    ancestor = ancestor.parentElement;
  }
}

export function KlaviyoPopupTheme() {
  useEffect(() => {
    // Klaviyo only injects one popup per page, so a single MutationObserver is
    // enough. Guard with a window-level flag so the shortcode bundle and the
    // webapp don't both attach observers when they happen to coexist.
    const w = window as WindowWithFlag;
    if (w[MOUNTED_FLAG]) return;
    w[MOUNTED_FLAG] = true;

    ensureHoverStylesheet();

    const patchAll = () => {
      document
        .querySelectorAll<HTMLFormElement>('form.klaviyo-form')
        .forEach(applyKlaviyoPopupTheme);
    };

    patchAll();

    const observer = new MutationObserver(patchAll);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      w[MOUNTED_FLAG] = false;
    };
  }, []);

  return null;
}

export default KlaviyoPopupTheme;
